/**
 * Income rider display helpers (T20-2222).
 *
 * The replacement-opportunity views need to answer three questions for each
 * policy: is income a feature, is it activated, and what is the income account
 * value. These helpers keep that derivation consistent between the list and
 * detail views.
 */

export interface PolicyIncomeFields {
  incomeStatus?: string | null;
  hasIncomeFeature?: boolean | null;
  hasIncomeRider?: boolean | null;
  incomeBenefitValue?: number | null;
}

/**
 * Is income a feature of the policy? True when the carrier flags an income
 * feature/rider, or when an income account value is present (which implies a
 * rider exists even if the boolean flags are missing from the mart).
 */
export function policyHasIncomeFeature(policy: PolicyIncomeFields): boolean {
  return (
    policy.hasIncomeFeature === true ||
    policy.hasIncomeRider === true ||
    (policy.incomeBenefitValue != null && policy.incomeBenefitValue > 0)
  );
}

/** Has income been activated (turned on) on the policy? */
export function policyIncomeActivated(policy: PolicyIncomeFields): boolean {
  return policy.incomeStatus === 'active';
}

/** Options for the income-status filter dropdown. */
export const INCOME_STATUS_FILTER_OPTIONS = [
  { value: 'active', label: 'Income Active' },
  { value: 'not_started', label: 'Income Not Started' },
] as const;

const INCOME_STATUS_FILTER_VALUES = new Set<string>(
  INCOME_STATUS_FILTER_OPTIONS.map((opt) => opt.value)
);

/** Is the value a supported income-status filter value (not a sentinel/garbage)? */
export function isIncomeStatusFilterValue(value: string | null): value is 'active' | 'not_started' {
  return !!value && INCOME_STATUS_FILTER_VALUES.has(value);
}
